import Modal from "@material-ui/core/Modal";
import React, { useState, useMemo } from "react";
import { Autocomplete } from "@material-ui/lab";
import { TextField } from "@material-ui/core";
import "../../assets/styles/TopBar.css";
import { useLogin } from "../../services/contexts/AdminLoginContext";
import { reassignRelationshipManager } from "../../services/apis/CRMApi";

const SEGMENTS = [
    { key: "high", label: "High" },
    { key: "low", label: "Low" },
    { key: "medium", label: "Medium" },
];

export default function ChangeRMModal({ open, onClose, adminList = [], oldRelationshipManagerId, onSuccess }) {
    const { token, setLoginError } = useLogin();
    const [segmentAssignments, setSegmentAssignments] = useState({
        high: [],
        low: [],
        medium: [],
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const rmAdmins = useMemo(() => (Array.isArray(adminList) ? adminList.filter((a) => a.can_become_rm) : []), [adminList]);

    const handleClose = () => {
        setError("");
        setSegmentAssignments({ high: [], low: [], medium: [] });
        onClose();
    };

    const getAvailableAdminIdsForSegment = (segmentKey) => {
        const usedInOthers = [
            ...segmentAssignments.high,
            ...segmentAssignments.low,
            ...segmentAssignments.medium,
        ].filter((id) => !segmentAssignments[segmentKey].includes(id));
        return usedInOthers;
    };

    const handleSegmentChange = (segmentKey, selectedAdminIds) => {
        const selectedSet = new Set(selectedAdminIds);
        const otherSegments = SEGMENTS.filter((s) => s.key !== segmentKey).map((s) => s.key);
        setSegmentAssignments((prev) => {
            const next = { ...prev, [segmentKey]: selectedAdminIds };
            otherSegments.forEach((seg) => {
                next[seg] = (prev[seg] || []).filter((id) => !selectedSet.has(id));
            });
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!oldRelationshipManagerId) {
            setError("Missing relationship manager.");
            return;
        }
        setError("");
        setSubmitting(true);
        try {
            const data = SEGMENTS
                .filter(({ key }) => (segmentAssignments[key] || []).length > 0)
                .map(({ key }) => ({
                    cx_segment: key,
                    admin_ids: segmentAssignments[key] || [],
                }));
            const payload = {
                old_relationship_manager_id: String(oldRelationshipManagerId),
                data,
            };
            const response = await reassignRelationshipManager(token, payload);
            if (response && response.status === 200) {
                handleClose();
                if (onSuccess) onSuccess();
            } else if (response && response.status === 401) {
                setLoginError("Unauthorized.");
            } else {
                setError(response?.data?.message || response?.data?.error || "Failed to reassign relationship managers.");
            }
        } catch (err) {
            setError("Failed to reassign relationship managers.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="modal-fade" tabIndex="-1" role="dialog" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header border-0">
                            <h5 className="modal-title">Change RM – Assign by customer segment</h5>
                            <button type="button" className="close" aria-label="Close" onClick={handleClose}>
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body overflow-auto" style={{ maxHeight: "70vh" }}>
                                <p className="text-muted small mb-3">Select admins for each customer segment. An admin can be assigned to only one segment.</p>
                                {SEGMENTS.map(({ key, label }) => {
                                    const usedElsewhere = getAvailableAdminIdsForSegment(key);
                                    const availableAdmins = rmAdmins.filter((a) => !usedElsewhere.includes(a.id));
                                    const selectedIds = segmentAssignments[key] || [];
                                    const selectedAdmins = rmAdmins.filter((a) => selectedIds.includes(a.id));
                                    return (
                                        <div key={key} className="form-group">
                                            <label className="font-weight-bold">Customer segment: {label}</label>
                                            <Autocomplete
                                                multiple
                                                disableCloseOnSelect
                                                filterSelectedOptions
                                                className="f-12"
                                                getOptionLabel={(option) => option.name ? `${option.name} (${option.email || ""})` : ""}
                                                onChange={(e, value) => {
                                                    handleSegmentChange(key, (value || []).map((a) => a.id));
                                                }}
                                                options={availableAdmins}
                                                value={selectedAdmins}
                                                renderInput={(params) => (
                                                    <TextField {...params} placeholder="Search and select admins" className="f-12" />
                                                )}
                                            />
                                        </div>
                                    );
                                })}
                                {error && <p className="text-danger mt-2">{error}</p>}
                            </div>
                            <div className="modal-footer border-0">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? "Submitting…" : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
